'use client';

import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useVisibleFieldDefinitions } from '../shared';
import {
  AssetHydrationContextProvider,
  type AssetHydrationQuery,
  type SetAssetsHydrated,
} from './contexts';
import {
  HydrateScrolledAssets,
  type HydrateScrolledAssetsQueryResult,
  type HydrateScrolledAssetsQueryVariables,
} from './gql/generated/HydrateScrolledAssets';
import {
  HydrateSettledAssets,
  type HydrateSettledAssetsQueryResult,
  type HydrateSettledAssetsQueryVariables,
} from './gql/generated/HydrateSettledAssets';
import {
  FIRST_PASS_SENTINEL,
  getAssetHydrated,
  getVisibleFieldIds,
  NO_FIELDS_SENTINEL,
  SCROLLED_DEBOUNCE,
  SETTLED_DEBONCE,
} from './utils';

type ScrolledVariables = Omit<
  HydrateScrolledAssetsQueryVariables,
  'ids' | 'page'
>;
type SettledVariables = Omit<
  HydrateSettledAssetsQueryVariables,
  'ids' | 'page'
>;

type HydrationQueryVariables = ScrolledVariables | SettledVariables;

export function AssetHydration({ children }: PropsWithChildren) {
  const accountId = useActiveAccountId() as string;
  const visibleFieldDefinitions = useVisibleFieldDefinitions();

  const queries = useMemo<
    AssetHydrationQuery<HydrationQueryVariables>[]
  >(() => {
    const visibleFieldIds = visibleFieldDefinitions?.map((def) => def.id) ?? [];

    return [
      {
        debounce: SCROLLED_DEBOUNCE,
        query: HydrateScrolledAssets,
        variables: { accountId, includeDeleted: true, visibleFieldIds },
      },
      {
        debounce: SETTLED_DEBONCE,
        query: HydrateSettledAssets,
        variables: { accountId },
      },
    ];
  }, [accountId, visibleFieldDefinitions]);

  const setAssetsHydrated = useCallback<SetAssetsHydrated>(
    (result, query, cache) => {
      const resultVisibleFieldIds = getVisibleFieldIds(query);

      const assets = (
        result as
          | HydrateScrolledAssetsQueryResult
          | HydrateSettledAssetsQueryResult
      ).data?.account?.assets?.nodes;

      assets?.forEach((asset) => {
        if (!asset) return;

        if (!cache.get(asset.id)) {
          cache.set(asset.id, new Set());
        }
        const entry = cache.get(asset.id);
        if (resultVisibleFieldIds === undefined) {
          entry!.add(FIRST_PASS_SENTINEL);
          return;
        }
        if (resultVisibleFieldIds.length === 0) {
          entry!.add(NO_FIELDS_SENTINEL);
          return;
        }
        resultVisibleFieldIds.forEach((fieldId) => {
          entry!.add(fieldId);
        });
      });
    },
    []
  );

  return (
    <AssetHydrationContextProvider
      getAssetHydrated={getAssetHydrated}
      queries={queries}
      setAssetsHydrated={setAssetsHydrated}
    >
      {children}
    </AssetHydrationContextProvider>
  );
}
